import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router';
import { Spacer, TextTitle } from '@pypestream/design-system';
import { StudioPageProps } from '../hooks/use-pro-studio-routes';
import { WithLoadingState } from '../router';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { getChannelsFromPypes, isCallRecordingEnabled } from '../utils';
import { VoiceChannelSettingsForm } from '../components';
import { VoiceChannelFormValues } from '../components/voice-channel-settings-form/voice-channel-settings-form';
import { CONTACT_CENTER_PLATFORM } from '../components/voice-channel-settings-form/build-voice-ai-config';
import {
  clampEotThreshold,
  EOT_THRESHOLD_DEFAULT,
} from '../components/voice-channel-settings-form/flux-rules';
import {
  DEFAULT_AUDIO_MODEL,
  DEFAULT_LOCALE,
  languageKeyOf,
  languagesFromTwilioConfig,
} from '../components/voice-channel-settings-form/languages-config';

const isAbsent = (v: unknown): boolean =>
  v == null || v === 'null' || v === 'undefined';

type VoiceChannelSettingsPageProps = StudioPageProps;

const VoiceChannelSettingsPage: React.FC<
  VoiceChannelSettingsPageProps
> = () => {
  const { channelId } = useParams();
  const location = useLocation();
  const isUpdating = !!channelId && !location.pathname.includes('/create');

  const { botPypes } = useProStudioCtxSelector((ctx) => ({
    botPypes: ctx.botPypes || [],
  }));

  const selectedChannel = useMemo(
    () =>
      isUpdating
        ? getChannelsFromPypes(botPypes).find(
            ({ id, type }) => id === channelId && type === 'twilio_voice'
          )
        : null,
    [botPypes, channelId, isUpdating]
  );

  const initialValues: VoiceChannelFormValues = useMemo(
    () =>
      selectedChannel
        ? {
            botName: selectedChannel?.name || '',
            // Overlay when Engagement would use it, otherwise one entry from
            // the flat fields — how every pre-PSTUDIO-2950 channel opens.
            ...languagesFromTwilioConfig(
              selectedChannel?.twilioVoiceConfig || {}
            ),
            env: selectedChannel?.env || 'sandbox',
            speechModel:
              selectedChannel?.twilioVoiceConfig?.speech_model || 'long',
            transcriptionProvider:
              selectedChannel?.twilioVoiceConfig?.transcription_provider ||
              'Google',
            accountSid: selectedChannel?.twilioVoiceConfig?.account_sid || '',
            authToken: selectedChannel?.twilioVoiceConfig?.auth_token || '',
            phoneNumber: selectedChannel?.twilioVoiceConfig?.phone_number || '',
            webhookUrl:
              selectedChannel?.twilioVoiceConfig?.init_call_webhook_url || '',
            laPlatform:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.integrator ||
              (selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.voice_inbox_id
                ? CONTACT_CENTER_PLATFORM
                : ''),
            laClientId:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.client_id || '',
            laClientSecret:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.client_secret || '',
            laGrantType:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.grant_type || '',
            laInstanceUrl:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.instance_url || '',
            laOAuthUrl:
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.o_auth_url || '',
            laAccountId: String(
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.account_id ?? ''
            ),
            laVoiceInboxId: String(
              selectedChannel?.twilioVoiceConfig?.live_agent_config
                ?.voice_inbox_id ?? ''
            ),
            handoffRules: (() => {
              const opts = selectedChannel?.twilioVoiceConfig?.handoff_options;
              if (!opts || Object.keys(opts).length === 0) {
                return [{ skillTag: 'general', destination: '' }];
              }
              const rules = Object.entries(opts).map(([skillTag, val]) => ({
                skillTag,
                destination: val.destination,
                type: (val.type as 'phone_number' | 'sip') ?? 'phone_number',
              }));
              const generalRule = rules.find((r) => r.skillTag === 'general');
              const otherRules = rules.filter((r) => r.skillTag !== 'general');
              return [
                generalRule ?? {
                  skillTag: 'general',
                  destination: '',
                  type: 'phone_number' as const,
                },
                ...otherRules,
              ];
            })(),
            inboundMessageBuffer: (() => {
              const v =
                selectedChannel?.twilioVoiceConfig?.inbound_message_buffer_ms;
              return isAbsent(v) ? 1000 : Number(v);
            })(),
            userSilenceTimeout: (() => {
              const v =
                selectedChannel?.twilioVoiceConfig?.user_silence_timeout;
              return isAbsent(v) ? null : Number(v);
            })(),
            eotThreshold: (() => {
              const v = selectedChannel?.twilioVoiceConfig?.eot_threshold;
              return isAbsent(v)
                ? EOT_THRESHOLD_DEFAULT
                : clampEotThreshold(Number(v));
            })(),
            promptMaxRetries: (() => {
              const v = selectedChannel?.twilioVoiceConfig?.prompt_max_retries;
              return isAbsent(v) ? undefined : v;
            })(),
            retryPromptPrefix: (() => {
              const v = selectedChannel?.twilioVoiceConfig?.retry_prompt_prefix;
              return isAbsent(v) ? undefined : v;
            })(),
            // Read through the helper, not by truthiness: the Channels API
            // stores an absent value as the STRING 'undefined' (PSTUDIO-4090).
            enableCallRecording: isCallRecordingEnabled(selectedChannel),
          }
        : {
            botName: '',
            env: 'sandbox',
            speechModel: 'long',
            transcriptionProvider: 'Google',
            languages: [
              {
                key: languageKeyOf(DEFAULT_LOCALE),
                locale: DEFAULT_LOCALE,
                elevenlabsAudioModel: DEFAULT_AUDIO_MODEL,
                elevenlabsVoiceId: '',
                elevenlabsSimilarity: 0.6,
                elevenlabsSpeed: 0.8,
                elevenlabsStability: 0.8,
              },
            ],
            startingLanguage: languageKeyOf(DEFAULT_LOCALE),
            accountSid: '',
            authToken: '',
            phoneNumber: '',
            webhookUrl: '',
            laPlatform: '',
            laClientId: '',
            laClientSecret: '',
            laGrantType: '',
            laInstanceUrl: '',
            laOAuthUrl: '',
            laAccountId: '',
            laVoiceInboxId: '',
            handoffRules: [
              {
                skillTag: 'general',
                destination: '',
                type: 'phone_number' as const,
              },
            ],
            inboundMessageBuffer: 1000,
            userSilenceTimeout: null,
            eotThreshold: EOT_THRESHOLD_DEFAULT,
            promptMaxRetries: undefined,
            retryPromptPrefix: "I'm sorry, I didn't quite catch that.",
            // Opt-in: a new channel does not record until someone says so.
            enableCallRecording: false,
          },
    [selectedChannel]
  );

  return (
    <>
      <TextTitle test-id="ps-voice-channel-settings-title" size="small">
        {isUpdating ? 'Channel Settings' : 'Create Voice Channel'}
      </TextTitle>
      <Spacer size="large" />
      <VoiceChannelSettingsForm
        type={isUpdating ? 'update' : 'create'}
        initialValues={initialValues}
        channelId={channelId}
      />
    </>
  );
};

export const VoiceChannelSettings = WithLoadingState(VoiceChannelSettingsPage);
